import { Text } from "@clipboard-health/ui-components";
import { Box, Skeleton } from "@mui/material";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { useQuery } from "@tanstack/react-query";
import { type ErrorInfo, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { isUnsupportedIosVersionForGoogleMaps } from "./isUnsupportedIosVersionForGoogleMaps";

interface MapErrorBoundaryProps {
  children: ReactNode;
}

function GoogleMapErrorBoundaryFallback() {
  const { data: isUnsupportedIosVersion, isLoading } = useQuery(
    ["isUnsupportedIosVersionForGoogleMaps"],
    async () => {
      return await isUnsupportedIosVersionForGoogleMaps();
    }
  );

  if (isLoading) {
    return <Skeleton height="100%" width="100%" />;
  }

  return (
    <Box
      sx={{
        px: 5,
        py: 4,
      }}
    >
      <Text>
        {isUnsupportedIosVersion
          ? "⚠️ Maps can’t load because Google Maps no longer supports your iOS version. You can still book shifts. Upgrade to iOS 16 or later for full map support."
          : "⚠️ The map failed to load. You can still book shifts. Try refreshing or check your connection."}
      </Text>
    </Box>
  );
}

function reportErrorToDataDog(error: Error, errorInfo: ErrorInfo): void {
  /**
   * Taken from https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors/?tab=npm#react-error-boundaries-instrumentation
   */
  const renderingError = new Error(error.message);

  renderingError.name = `ReactRenderingError`;
  renderingError.stack = errorInfo.componentStack;
  // We need "as any" here because although DataDog understands "cause"
  // for TypeScript is not a valid property of Error.
  (renderingError as any).cause = error;

  logError(APP_V2_APP_EVENTS.GOOGLE_MAPS_RENDER_ERROR, { error: renderingError });
}

export function GoogleMapErrorBoundary(props: MapErrorBoundaryProps) {
  const { children } = props;

  return (
    <ErrorBoundary
      FallbackComponent={GoogleMapErrorBoundaryFallback}
      onError={(error, errorInfo) => {
        reportErrorToDataDog(error, errorInfo);
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
